import { useEffect, useState, useMemo, useCallback } from "react";
import {
   Table, Button, Modal, Form, Input, message,
   Popconfirm, Space, Row, Col, Tag, Tooltip
} from "antd";
import {
   EditOutlined, DeleteOutlined, PlusOutlined,
   ProjectOutlined, TeamOutlined, SearchOutlined,
   ExclamationCircleOutlined, CheckCircleOutlined,
   FolderOpenOutlined
} from "@ant-design/icons";
import {
   getAllProjects,
   createProject,
   updateProject,
   deleteProject
} from "../../services/projectsApi";

// ─── Styles ───
const styles = `
   .projects-card {
      background: #fff;
      border-radius: 10px;
      box-shadow: 0 1px 2px 0 rgba(0,0,0,0.03);
      border: 1px solid #f3f4f6;
   }
   .projects-table .ant-table-thead > tr > th {
      background: #f9fafb;
      font-size: 12px;
      padding: 10px 16px;
   }
   .projects-table .ant-table-tbody > tr > td {
      padding: 10px 16px;
      font-size: 13px;
   }
   .projects-table .ant-table-tbody > tr:hover > td {
      background: #f9fafb;
   }
   .stat-card {
      border-radius: 10px;
      transition: all 0.3s;
   }
   .stat-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
   }
`;

// ─── Helper Functions ───
const getLeadCountColor = (count) => {
   if (count >= 20) return "green";
   if (count >= 10) return "blue";
   if (count >= 5) return "orange";
   return "default";
};

// ─── Main Component ───
const Projects = () => {
   const [projects, setProjects] = useState([]);
   const [loading, setLoading] = useState(false);
   const [open, setOpen] = useState(false);
   const [editingProject, setEditingProject] = useState(null);
   const [searchText, setSearchText] = useState("");
   const [submitLoading, setSubmitLoading] = useState(false);
   const [form] = Form.useForm();

   // ─── Fetch Projects ───
   const fetchProjects = useCallback(async () => {
      setLoading(true);
      try {
         const { data } = await getAllProjects();
         setProjects(data);
      } catch (error) {
         message.error("Failed to fetch projects");
      } finally {
         setLoading(false);
      }
   }, []);

   useEffect(() => {
      fetchProjects();
   }, [fetchProjects]);

   // ─── Submit Handler ───
   const handleSubmit = async (values) => {
      setSubmitLoading(true);
      try {
         if (editingProject) {
            await updateProject(editingProject.id, values);
            message.success("Project updated successfully");
         } else {
            await createProject(values);
            message.success("Project created successfully");
         }
         handleCloseModal();
         fetchProjects();
      } catch (error) {
         message.error("Operation failed");
      } finally {
         setSubmitLoading(false);
      }
   };

   // ─── Delete Handler ───
   const handleDelete = async (id) => {
      try {
         await deleteProject(id);
         message.success("Project deleted");
         fetchProjects();
      } catch (error) {
         message.error("Delete failed");
      }
   };

   // ─── Modal Handlers ───
   const handleCloseModal = () => {
      setOpen(false);
      setEditingProject(null);
      form.resetFields();
   };

   const handleEdit = (record) => {
      setEditingProject(record);
      form.setFieldsValue(record);
      setOpen(true);
   };

   // ─── Filtered Data ───
   const filteredProjects = useMemo(() => {
      return projects.filter((project) =>
         project.name?.toLowerCase().includes(searchText.toLowerCase())
      );
   }, [projects, searchText]);

   // ─── Stats ───
   const stats = useMemo(() => {
      const totalLeads = projects.reduce((acc, p) => acc + (p.lead_count || 0), 0);
      const activeProjects = projects.filter((p) => p.lead_count > 0).length;
      return { totalLeads, activeProjects, total: projects.length };
   }, [projects]);

   // ─── Table Columns ───
   const columns = [
      {
         title: "#",
         width: 50,
         align: "center",
         render: (_, __, index) => (
            <span className="text-xs text-gray-400">{index + 1}</span>
         )
      },
      {
         title: "Project Name",
         dataIndex: "name",
         width: 130,
         render: (name) => (
            <div className="flex items-center gap-3">
               <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm">
                  <ProjectOutlined className="text-white text-sm" />
               </div>
               <span className="font-medium text-sm text-gray-800">{name}</span>
            </div>
         )
      },
      {
         title: "Total Leads",
         dataIndex: "lead_count",
         width: 130,
         render: (count) => (
            <Tag
               color={getLeadCountColor(count)}
               style={{ margin: 0, fontSize: '11px' }}
               icon={<TeamOutlined />}
            >
               {count || 0} leads
            </Tag>
         )
      },
      {
         title: "Status",
         width: 100,
         render: (_, record) => (
            <Tag
               color={record.lead_count > 0 ? "green" : "default"}
               style={{ margin: 0, fontSize: '11px' }}
               icon={record.lead_count > 0 ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
            >
               {record.lead_count > 0 ? "Active" : "Inactive"}
            </Tag>
         )
      },
      {
         title: "Actions",
         width: 100,
         align: "center",
         render: (_, record) => (
            <Space size="small">
               <Tooltip title="Edit">
                  <Button
                     type="text"
                     size="small"
                     icon={<EditOutlined />}
                     onClick={() => handleEdit(record)}
                  />
               </Tooltip>
               <Popconfirm
                  title="Delete Project?"
                  description="Are you sure you want to delete this project?"
                  icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
               >
                  <Tooltip title="Delete">
                     <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Tooltip>
               </Popconfirm>
            </Space>
         )
      }
   ];

   return (
      <div className="bg-gray-50 min-h-screen">
         <style>{styles}</style>

         {/* Header */}
         <div className="bg-white border-b px-6 py-4">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-xl font-semibold text-gray-800 m-0">Projects</h1>
                  <span className="text-gray-400 text-sm">
                     Manage and organize all your projects • {stats.total} total
                  </span>
               </div>
               <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setOpen(true)}
               >
                  Add Project
               </Button>
            </div>
         </div>

         <div className="p-6">
            {/* Stats Cards */}
            <Row gutter={[16, 16]} className="mb-4">
               <Col xs={24} sm={8}>
                  <div className="projects-card p-4 stat-card">
                     <div className="flex items-center justify-between">
                        <div>
                           <div className="flex items-center gap-2 text-indigo-600 mb-2">
                              <FolderOpenOutlined />
                              <span className="text-sm font-medium">Total Projects</span>
                           </div>
                           <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                           <ProjectOutlined className="text-indigo-500 text-lg" />
                        </div>
                     </div>
                  </div>
               </Col>

               <Col xs={24} sm={8}>
                  <div className="projects-card p-4 stat-card">
                     <div className="flex items-center justify-between">
                        <div>
                           <div className="flex items-center gap-2 text-green-600 mb-2">
                              <CheckCircleOutlined />
                              <span className="text-sm font-medium">Active Projects</span>
                           </div>
                           <div className="text-2xl font-bold text-gray-800">
                              {stats.activeProjects}
                              <span className="text-sm text-gray-400 ml-2">/ {stats.total}</span>
                           </div>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                           <CheckCircleOutlined className="text-green-500 text-lg" />
                        </div>
                     </div>
                  </div>
               </Col>

               <Col xs={24} sm={8}>
                  <div className="projects-card p-4 stat-card">
                     <div className="flex items-center justify-between">
                        <div>
                           <div className="flex items-center gap-2 text-blue-600 mb-2">
                              <TeamOutlined />
                              <span className="text-sm font-medium">Total Leads</span>
                           </div>
                           <div className="text-2xl font-bold text-gray-800">{stats.totalLeads}</div>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                           <TeamOutlined className="text-blue-500 text-lg" />
                        </div>
                     </div>
                  </div>
               </Col>
            </Row>

            {/* Table Card */}
            <div className="projects-card">
               <div className="p-4 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                     <ProjectOutlined className="text-gray-600" />
                     <span className="font-medium text-gray-700">All Projects</span>
                     <Tag>{filteredProjects.length}</Tag>
                  </div>
                  <Input.Search
                     placeholder="Search projects..."
                     allowClear
                     style={{ width: 250 }}
                     value={searchText}
                     onChange={(e) => setSearchText(e.target.value)}
                     prefix={<SearchOutlined className="text-gray-400" />}
                  />
               </div>

               <div className="projects-table">
                  <Table
                     size="small"
                     dataSource={filteredProjects}
                     columns={columns}
                     rowKey="id"
                     loading={loading}
                     pagination={{
                        pageSize: 10,
                        showTotal: (total, range) => (
                           <span className="text-sm text-gray-500">
                              Showing <b>{range[0]}-{range[1]}</b> of <b>{total}</b> projects
                           </span>
                        ),
                        size: "small"
                     }}
                     locale={{
                        emptyText: (
                           <div className="py-8 text-center">
                              <ProjectOutlined className="text-4xl text-gray-300 mb-3" />
                              <p className="text-gray-500 mb-3">No projects found</p>
                              <Button
                                 type="primary"
                                 size="small"
                                 icon={<PlusOutlined />}
                                 onClick={() => setOpen(true)}
                              >
                                 Create First Project
                              </Button>
                           </div>
                        )
                     }}
                  />
               </div>
            </div>
         </div>

         {/* Modal */}
         <Modal
            title={editingProject ? "Edit Project" : "Add New Project"}
            open={open}
            onCancel={handleCloseModal}
            footer={null}
            width={400}
            destroyOnClose
         >
            <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">
               <Form.Item
                  name="name"
                  label="Project Name"
                  rules={[
                     { required: true, message: "Please enter project name" },
                     { min: 3, message: "Minimum 3 characters required" }
                  ]}
               >
                  <Input
                     placeholder="Enter project name"
                     prefix={<ProjectOutlined className="text-gray-400" />}
                  />
               </Form.Item>

               <div className="flex justify-end gap-2">
                  <Button onClick={handleCloseModal}>Cancel</Button>
                  <Button
                     type="primary"
                     htmlType="submit"
                     loading={submitLoading}
                     icon={editingProject ? <EditOutlined /> : <PlusOutlined />}
                  >
                     {editingProject ? "Update" : "Create"}
                  </Button>
               </div>
            </Form>
         </Modal>
      </div>
   );
};

export default Projects;